import {useContext, useEffect, useMemo} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft} from "@fortawesome/pro-regular-svg-icons/faArrowLeft";
import {faFileExport} from "@fortawesome/pro-regular-svg-icons/faFileExport";
import dayjs from "dayjs";
import {isEmpty} from "es-toolkit/compat";

import RenderIf from "@/components/Global/RenderIf/RenderIf";
import Button from "@/components/Global/Button/Button";
import ExportResults from "@/components/Results/ExportResults/ExportResults";
import {getReadableTextColor} from "@/helpers/getReadableTextColor.js";
import {closeBootstrapModal} from "@/helpers/closeBootstrapModal.js";
import {checkIsDemoAccount} from "@/helpers/checkIsDemoAccount";
import {useResults} from "@/hooks/useResults/useResults.js";
import {useMediaQuery} from "@/hooks/useMediaQuery/useMediaQuery";
import {AppVisibilityContext} from "@/contexts/AppVisibilityContext";
import HeaderWithTabs from "@/components/Global/Header/Variants/HeaderWithTabs/HeaderWithTabs";

import {config} from "@/config";

import styles from "./TestResultHeader.module.scss";
import {selectUserDetails} from "@/features/user/userSelectors";
import {selectOrdersData} from "@/features/orders/ordersSelector";

export default function TestResultHeader({
  handleBack,
  showTabs = false,
  onTabClick,
  showExportButton = true,
  backButtonText,
  isSingleBiomarkerHeader = false,
}) {
  let {orderId} = useParams();
  orderId = parseInt(orderId);

  const navigate = useNavigate();
  const {filteredResult} = useResults({orderId});
  const customerDetails = useSelector(selectUserDetails);
  const orders = useSelector(selectOrdersData);

  const {appVisibility} = useContext(AppVisibilityContext);
  const modalId = "export-results";

  const isWhiteLabelApp = window.config.isWhiteLabelApp;
  const isForth = config.websiteId == 1;
  const isEdge = config.websiteId == 2;
  const isBiomarkerTracking = config.websiteId == 3 && !isWhiteLabelApp;

  useEffect(() => {
    return () => {
      closeBootstrapModal(modalId);
    };
  }, []);

  const handleScroll = e => {
    document.documentElement.scroll({top: 0, behavior: "smooth"});
  };

  const isDemoAccount = checkIsDemoAccount(customerDetails?.ID);
  const isFemaleResult = filteredResult.isFemaleHormoneTest == 1;
  const healthCategories = filteredResult?.healthScores || [];
  const showHealthScoreTab = filteredResult.showHCReports && !isEmpty(healthCategories) && appVisibility.healthScores;

  const {isDesktop} = useMediaQuery();

  const activeHeaderTab = new URLSearchParams(window.location.search).get("tab") || "your-results";
  const headerTabs = [
    {
      id: "your-results",
      label: "Your results",
      ariaLabel: "your results",
      isActive: activeHeaderTab === "your-results",
      show: !isDesktop && showTabs,
      onClick: onTabClick,
    },
    {
      id: "health-scores",
      label: "Health scores",
      ariaLabel: "health scores",
      isActive: activeHeaderTab === "health-scores",
      show: showHealthScoreTab && !isDesktop && showTabs,
      onClick: onTabClick,
    },
    {
      id: "all-markers",
      label: "All markers",
      ariaLabel: "all markers",
      isActive: activeHeaderTab === "all-markers",
      show: !isDesktop && showTabs,
      onClick: onTabClick,
    },
  ];

  const orderReference = useMemo(() => {
    if (isEmpty(filteredResult)) return "";
    if (filteredResult?.isFemaleHormoneTest != 1) return filteredResult.orderReference;

    const [orderNumber, kit] = filteredResult?.orderReference?.split("-") || [];

    const siblingOrder = orders.find(
      order =>
        order.orderReference.startsWith(orderNumber) &&
        order.isFemaleHormoneTest == 1 &&
        order.isDoubleKit &&
        order.orderId != filteredResult.orderId,
    );

    const [siblingOrderNumber = "", siblingKit = ""] = (siblingOrder?.orderReference || "").split("-");

    return `${filteredResult.orderReference}${siblingKit && !filteredResult.orderReference.includes(siblingKit) ? `/${siblingKit}` : ""}`;
  }, [filteredResult]);

  const whiteLabelSubTitleColor = useMemo(() => {
    if (!isWhiteLabelApp) return {};

    let bgHeader = document.documentElement.style.getPropertyValue("--bg-header") || "#ffffff";
    if (bgHeader.startsWith("var(")) {
      const bgHeaderVar = bgHeader.split("var(")[1].split(")")[0];
      bgHeader = getComputedStyle(document.documentElement).getPropertyValue(bgHeaderVar);
    }

    return {color: getReadableTextColor(bgHeader)};
  }, [isWhiteLabelApp]);

  return (
    <HeaderWithTabs tabs={isSingleBiomarkerHeader ? [] : headerTabs} className="py-1">
      <div className={"row g-0 px-2 py-3 align-items-center"}>
        <div className="col-auto">
          <Button variant="transparent" className="p-0" onClick={() => navigate("/results")} aria-label="go back">
            <FontAwesomeIcon icon={faArrowLeft} size="lg" className="ps-1" /> {backButtonText}
          </Button>
        </div>
        <div className="col text-center">
          <h3 className={"fw-semibold px-2 mb-1"} dangerouslySetInnerHTML={{__html: filteredResult.testName}}></h3>
          <p
            className={`fw-normal mb-0 ${isEdge ? "text-white" : isForth || isBiomarkerTracking ? "text-muted" : ""}`}
            style={whiteLabelSubTitleColor}>
            {orderReference} | {dayjs(filteredResult.orderCompleteDate).format("DD/MM/YY")}
          </p>
        </div>
        <RenderIf isTrue={!isDemoAccount && showExportButton}>
          <div className="col-auto">
            <Button variant="transparent" className="p-0" data-bs-toggle="modal" data-bs-target={`#${modalId}`}>
              <FontAwesomeIcon size="xl" icon={faFileExport} />
              <p className={`fw-normal ${styles.shareBtn} mb-0 mt-1`}>Export</p>
            </Button>
            <ExportResults modalId={modalId} isFemaleResult={isFemaleResult} />
          </div>
        </RenderIf>
      </div>
    </HeaderWithTabs>
  );
}
